/**
 * @fileoverview Pure helpers behind the org Dashboard.
 *
 * Every number the page prints is computed by `GET /org/analytics` under the
 * one taught rule the program and class pages use; these helpers only turn it
 * into words: a change between two windows, a percentage, a month label, an
 * axis, the date range the director chose, and the short list of things that
 * need someone's attention. Kept out of the component so each rule is tested
 * once, without a render.
 */

/** How far back a class meeting can be owed notes (`org_analytics.NOTES_OWED_DAYS`). */
export const NOTES_WINDOW_DAYS = 60;

/** The labels the program pages use for a program's type. */
export const PROGRAM_TYPE_LABELS = { private_lessons: "Private lessons", class: "Class", camp: "Camp" };

/**
 * The ranges a director can read the dashboard over. The server resolves each
 * one against the org's own "today" (`org_analytics.resolve_range`), so "this
 * year" starts on the org's January 1st rather than the browser's.
 */
export const RANGES = [
  { id: "30d", label: "Past 30 days" },
  { id: "12m", label: "Past 12 months" },
  { id: "ytd", label: "This year" },
  { id: "last_year", label: "Last year" },
  { id: "all", label: "All time" },
  { id: "custom", label: "Custom range" },
];

export const DEFAULT_RANGE = "30d";

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A range as the URL carries it, cleaned: an unknown id reads as the default,
 * and a custom range without two ordered dates is not a range yet.
 *
 * @returns {{id: string, start: string|null, end: string|null}}
 */
export function readRange(id, start, end) {
  const known = RANGES.some((r) => r.id === id) ? id : DEFAULT_RANGE;
  if (known !== "custom") return { id: known, start: null, end: null };
  if (!ISO_DAY.test(start || "") || !ISO_DAY.test(end || "") || start > end) {
    return { id: DEFAULT_RANGE, start: null, end: null };
  }
  return { id: "custom", start, end };
}

/** The analytics and export query for a range: `period=…`, plus its dates when custom. */
export function rangeQuery(range) {
  const params = new URLSearchParams({ period: range?.id || DEFAULT_RANGE });
  if (range?.id === "custom") {
    params.set("start", range.start);
    params.set("end", range.end);
  }
  return params.toString();
}

/**
 * "vs prior 30 days", "vs same period last year" — what a change is measured
 * against, or null for a range with nothing before it (all time).
 */
export function comparisonLabel(period) {
  if (!period) return "vs prior 30 days";
  if (!period.previous) return null;
  return `vs ${period.previous.label || "the period before"}`;
}

/** A thousands-separated count, or an em dash for a missing one. */
export function formatCount(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString();
}

/** `part` as a whole percentage of `whole`, or null when there is no whole. */
export function percent(part, whole) {
  if (!whole) return null;
  return Math.round(((Number(part) || 0) / whole) * 100);
}

/**
 * The change from one window to the next.
 *
 * `pct` is null when the earlier window had nothing, because a percentage of
 * zero is not a number anyone can read: the change is then "new" rather than
 * "+∞%".
 *
 * @returns {{direction: "up"|"down"|"flat", pct: number|null, diff: number}}
 */
export function change(current, previous) {
  const cur = Number(current) || 0;
  const prev = Number(previous) || 0;
  if (cur === prev) return { direction: "flat", pct: 0, diff: 0 };
  if (!prev) return { direction: "up", pct: null, diff: cur };
  return { direction: cur > prev ? "up" : "down", pct: Math.round(((cur - prev) / prev) * 100), diff: cur - prev };
}

/** "+12%", "−8%", "New" or "No change" — a change in one short phrase. */
export function changeLabel(c) {
  if (c.direction === "flat") return "No change";
  if (c.pct === null) return "New";
  if (c.pct === 0) return c.direction === "up" ? "+<1%" : "−<1%";
  return `${c.pct > 0 ? "+" : "−"}${Math.abs(c.pct)}%`;
}

/** "Sep" (or "Sep 2026") from `YYYY-MM`, read in UTC so no zone shifts it. */
export function monthLabel(month, { year = false, long = false } = {}) {
  const [y, m] = String(month || "").split("-").map(Number);
  if (!y || !m) return month || "";
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, {
    month: long ? "long" : "short",
    ...(year ? { year: "numeric" } : {}),
    timeZone: "UTC",
  });
}

/** "Sep 22" (or "Sep 22, 2026") from `YYYY-MM-DD`, without UTC parsing shifting the day. */
export function dayLabel(ymd, { year = false } = {}) {
  const [y, m, d] = String(ymd || "").split("-").map(Number);
  if (!y || !m || !d) return ymd || "";
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(year ? { year: "numeric" } : {}),
  });
}

/**
 * A clean axis for a column chart: a round top and evenly spaced ticks.
 * Steps are 1, 2, 2.5 or 5 times a power of ten, so the gridlines read as
 * 0 / 50 / 100 / 150 rather than 0 / 47 / 94 / 141.
 */
export function niceScale(max, count = 4) {
  const top = Math.max(1, Number(max) || 0);
  const raw = top / count;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((f) => f * magnitude).find((s) => s >= raw) || raw;
  // Whole sessions only: a step of 0.5 would print half a lesson on the axis.
  const whole = Math.max(1, Math.ceil(step));
  const ticks = [];
  for (let v = 0; v <= whole * count; v += whole) ticks.push(v);
  return { max: whole * count, step: whole, ticks };
}

/** How each bucket size reads in a sentence. */
export const UNIT_WORDS = {
  week: { one: "week", each: "Weekly" },
  month: { one: "month", each: "Monthly" },
  year: { one: "year", each: "Yearly" },
};

/**
 * The chart's buckets for the chosen range, whatever the server sent.
 *
 * `series` is the range-aware shape; `months` is the older twelve-month one,
 * read as months whose last is still in progress, so a response from before
 * ranges existed still draws.
 *
 * @returns {{unit: "week"|"month"|"year", buckets: Array<{key: string, start?: string, end?: string, lessons: number, class_sessions: number, solo: number, emails: number, partial: boolean}>}}
 */
export function seriesBuckets(data) {
  if (data?.series?.buckets) return { unit: data.series.unit || "month", buckets: data.series.buckets };
  const months = data?.months || [];
  return {
    unit: "month",
    buckets: months.map((m, i) => ({ ...m, key: m.month, partial: i === months.length - 1 })),
  };
}

/**
 * A bucket's name: "Sep 1" for a week (its first day), "Sep" for a month,
 * "2026" for a year. `long` is the tooltip's and the table's fuller form.
 */
export function bucketLabel(bucket, unit, { year = false, long = false } = {}) {
  const key = bucket?.key || bucket?.month || "";
  if (unit === "year") return key;
  if (unit === "week") {
    const first = bucket?.start || key;
    return long ? `Week of ${dayLabel(first, { year })}` : dayLabel(first, { year });
  }
  return monthLabel(key, { year, long });
}

/**
 * The figures that sit above the chart.
 *
 * A bucket still in progress (`partial`) is left out of the average: a month
 * that is a week old would otherwise drag every average down. Rows without a
 * `partial` flag are the older monthly series, whose last month is the one in
 * progress. `current` is that bucket, or null when the range has ended.
 */
export function seriesSummary(buckets = []) {
  const flagged = buckets.some((b) => b.partial !== undefined);
  const inProgress = (b, i) => (flagged ? Boolean(b.partial) : i === buckets.length - 1);
  const total = buckets.reduce((sum, b) => sum + (b.lessons || 0), 0);
  const complete = buckets.filter((b, i) => !inProgress(b, i));
  const average = complete.length ? Math.round(complete.reduce((s, b) => s + (b.lessons || 0), 0) / complete.length) : null;
  const busiest = buckets.reduce((best, b) => (!best || (b.lessons || 0) > best.lessons ? b : best), null);
  const last = buckets[buckets.length - 1];
  return {
    total,
    average,
    busiest: busiest && busiest.lessons > 0 ? busiest : null,
    current: last && inProgress(last, buckets.length - 1) ? last : null,
    emails: buckets.reduce((sum, b) => sum + (b.emails || 0), 0),
  };
}

/**
 * The short list at the top of the Overview: what needs someone this week.
 *
 * Each item is work outstanding, not a failure, and says where to go to act
 * on it — a tab on this page, or the page that holds the rows. Items with
 * nothing to count are left out, so an empty list is a real all-clear except
 * where `notes_complete` says a connected class could not be checked, which is
 * reported as its own line rather than hidden in a zero.
 *
 * @returns {Array<{id: string, count: number|null, title: string, detail?: string, icon: string, tab?: string, filter?: string, path?: string}>}
 */
export function attentionItems(data) {
  if (!data) return [];
  const programs = data.by_program || [];
  const running = programs.filter((p) => p.lifecycle !== "past");
  const items = [];
  const one = (n, singular, plural) => (n === 1 ? singular : plural);
  // A single program with the problem goes straight to that program; several
  // go to the tab that lists them side by side.
  const target = (rows) =>
    rows.length === 1 ? { path: `/org/programs/${rows[0].program_id}` } : { tab: "programs" };

  // Substitute requests are the one item an instructor is actively waiting
  // on, so they lead (docs/substitute-requests.md).
  const subRequests = data.substitute_requests_pending || 0;
  if (subRequests > 0) {
    const byProgram = data.substitute_requests_pending_by_program || [];
    items.push({
      id: "substitute-requests",
      count: subRequests,
      icon: "users",
      title: `Substitute ${one(subRequests, "request", "requests")} to review`,
      detail:
        byProgram.length === 1
          ? byProgram[0].title
          : byProgram.length > 1
            ? `Across ${byProgram.length} programs`
            : "Instructors are waiting on an answer",
      ...(byProgram.length >= 1 ? { path: `/org/programs/${byProgram[0].program_id}` } : { path: "/org/programs" }),
    });
  }

  // Instructors asking to clear a missing-notes notice wait on an answer too
  // (lib/notesClearance). They land on the missing sessions, where each ask is
  // tagged and can be cleared or declined in bulk.
  const asking = programs.filter((p) => (p.notes_clear_requests || 0) > 0);
  const asks = asking.reduce((sum, p) => sum + p.notes_clear_requests, 0);
  if (asks > 0) {
    items.push({
      id: "notes-clear-requests",
      count: asks,
      icon: "fileText",
      title: `${one(asks, "Request", "Requests")} to clear missing notes`,
      detail: asking.length === 1 ? asking[0].title : `Across ${asking.length} programs`,
      ...(asking.length === 1
        ? { path: `/org/programs/${asking[0].program_id}?tab=sessions&filter=missing` }
        : { tab: "lessons", filter: "missing" }),
    });
  }

  const owing = programs.filter((p) => (p.notes_owed || 0) > 0);
  const owed = owing.reduce((sum, p) => sum + p.notes_owed, 0);
  if (owed > 0) {
    items.push({
      id: "notes",
      count: owed,
      icon: "fileText",
      title: `${one(owed, "Class meeting", "Class meetings")} with no notes`,
      detail:
        owing.length === 1
          ? `${owing[0].title} · last ${NOTES_WINDOW_DAYS} days`
          : `Across ${owing.length} programs · last ${NOTES_WINDOW_DAYS} days`,
      ...target(owing),
    });
  }

  const unstaffed = running.filter((p) => (p.unassigned_classes || 0) > 0);
  const unassigned = unstaffed.reduce((sum, p) => sum + p.unassigned_classes, 0);
  if (unassigned > 0) {
    items.push({
      id: "unassigned",
      count: unassigned,
      icon: "users",
      title: `${one(unassigned, "Class", "Classes")} with no instructor`,
      detail: unstaffed.length === 1 ? unstaffed[0].title : `Across ${unstaffed.length} programs`,
      ...target(unstaffed),
    });
  }

  const applying = running.filter((p) => (p.pending_applications || 0) > 0);
  const applications = applying.reduce((sum, p) => sum + p.pending_applications, 0);
  if (applications > 0) {
    items.push({
      id: "applications",
      count: applications,
      icon: "mail",
      title: `Instructor ${one(applications, "application", "applications")} to review`,
      detail: applying.length === 1 ? applying[0].title : `Across ${applying.length} programs`,
      ...target(applying),
    });
  }

  const quiet = running.filter((p) => !p.sessions_30d);
  if (quiet.length > 0) {
    items.push({
      id: "quiet-programs",
      count: quiet.length,
      icon: "grid",
      title: `Running ${one(quiet.length, "program", "programs")} with no sessions in 30 days`,
      detail: quiet.length === 1 ? quiet[0].title : quiet.slice(0, 2).map((p) => p.title).join(", ") + (quiet.length > 2 ? "…" : ""),
      ...target(quiet),
    });
  }

  const idle = (data.by_instructor || []).filter((i) => i.member && !i.sessions_30d);
  if (idle.length > 0) {
    items.push({
      id: "idle-instructors",
      count: idle.length,
      icon: "clock",
      title: `${one(idle.length, "Instructor", "Instructors")} with no sessions in 30 days`,
      detail: idle.slice(0, 2).map((i) => i.name).join(", ") + (idle.length > 2 ? "…" : ""),
      tab: "instructors",
      filter: "idle",
    });
  }

  if (data.consent?.stub_count > 0) {
    items.push({
      id: "imports",
      count: data.consent.stub_count,
      icon: "graduation",
      title: `Imported ${one(data.consent.stub_count, "student", "students")} to review`,
      detail: "Placeholders from scheduling, waiting for someone to confirm them",
      path: "/org/usage",
    });
  }

  const sms = data.sms;
  const smsUsed = sms ? percent(sms.sent, sms.allowance) : null;
  if (smsUsed !== null && smsUsed >= 80) {
    items.push({
      id: "sms",
      count: null,
      icon: "message",
      title: `Text reminder allowance ${smsUsed}% used`,
      detail: `${formatCount(sms.remaining)} left this month · reminders pause when it runs out`,
    });
  }

  if (programs.some((p) => p.notes_complete === false)) {
    items.push({
      id: "notes-incomplete",
      count: null,
      icon: "info",
      title: "Some classes are scheduled in Kalver Scheduler",
      detail: "Hans can't check those meetings for missing notes",
    });
  }
  return items;
}
